/**
 * File validation utilities for images and documents
 * Matches backend validation constraints from app/files/service.py
 */

// Image file extensions (for photos, avatars)
export const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp'];
export const IMAGE_MIME_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/bmp'];

// Document file extensions
export const DOCUMENT_EXTENSIONS = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.txt', '.csv'];
export const DOCUMENT_MIME_TYPES = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/plain',
    'text/csv'
];

// Maximum file size: 10MB (matches backend)
export const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10MB
export const MAX_FILE_SIZE_MB = 10;

export interface FileValidationResult {
    isValid: boolean;
    errorKey?: string; // Translation key for error message
}

/**
 * Validates if a file is a valid image
 * @param file - File to validate
 * @returns Validation result with error key if invalid
 */
export const validateImageFile = (file: File): FileValidationResult => {
    const fileName = file.name.toLowerCase();
    const fileType = file.type.toLowerCase();

    // Check file extension
    const hasValidExtension = IMAGE_EXTENSIONS.some(ext => fileName.endsWith(ext));

    // Check MIME type
    const hasValidMimeType = IMAGE_MIME_TYPES.some(mime => fileType === mime);

    if (!hasValidExtension && !hasValidMimeType) {
        return {
            isValid: false,
            errorKey: 'invalid_image_format'
        };
    }

    // Check file size
    if (file.size > MAX_FILE_SIZE_BYTES) {
        return {
            isValid: false,
            errorKey: 'invalid_image_size'
        };
    }

    return { isValid: true };
};

/**
 * Validates if a file is a valid document
 * @param file - File to validate
 * @returns Validation result with error key if invalid
 */
export const validateDocumentFile = (file: File): FileValidationResult => {
    const fileName = file.name.toLowerCase();
    const fileType = file.type.toLowerCase();

    // Check file extension
    const hasValidExtension = DOCUMENT_EXTENSIONS.some(ext => fileName.endsWith(ext));

    // Check MIME type (more lenient for documents due to browser inconsistencies)
    const hasValidMimeType = DOCUMENT_MIME_TYPES.some(mime => fileType === mime);

    if (!hasValidExtension && !hasValidMimeType) {
        return {
            isValid: false,
            errorKey: 'invalid_document_format'
        };
    }

    // Check file size
    if (file.size > MAX_FILE_SIZE_BYTES) {
        return {
            isValid: false,
            errorKey: 'invalid_document_size'
        };
    }

    return { isValid: true };
};

/**
 * Get formatted list of allowed image extensions for display
 */
export const getImageExtensionsDisplay = (): string => {
    return IMAGE_EXTENSIONS.map((ext: any) => ext.replace('.', '')).join(', ');
};

/**
 * Get formatted list of allowed document extensions for display
 */
export const getDocumentExtensionsDisplay = (): string => {
    return DOCUMENT_EXTENSIONS.map((ext: any) => ext.replace('.', '')).join(', ');
};
